'use client';

import React, { useEffect, useState, useRef } from 'react';
import { useRouter } from 'next/navigation';

interface NextArticleLoaderProps {
  currentSlug: string;
  categorySlug: string;
  nextArticle?: {
    title: string;
    slug: { current: string };
    description?: string;
    author?: { name: string };
    categories?: { title: string }[];
  };
}

export default function NextArticleLoader({ nextArticle }: NextArticleLoaderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [countdown, setCountdown] = useState(4);
  const loaderRef = useRef<HTMLDivElement>(null);
  const hasTriggered = useRef(false);
  const router = useRouter();

  useEffect(() => {
    if (!nextArticle || hasTriggered.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasTriggered.current) {
          hasTriggered.current = true;
          setIsVisible(true);

          // Countdown timer: 4 → 3 → 2 → 1 → navigate
          let count = 4;
          const countInterval = setInterval(() => {
            count -= 1;
            setCountdown(count);
            if (count <= 0) {
              clearInterval(countInterval);
              router.push(`/post/${nextArticle.slug.current}`);
            }
          }, 1000);
        }
      },
      { root: null, rootMargin: '0px', threshold: 0.3 }
    );

    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [nextArticle, router]);

  if (!nextArticle) return null;

  const category = nextArticle.categories?.[0]?.title;
  const progress = isVisible ? ((4 - countdown) / 4) * 100 : 0;

  return (
    <div ref={loaderRef} className="my-16 print:hidden">

      {/* Section divider */}
      <div className="flex items-center gap-4 mb-5">
        <div className="h-px flex-1 bg-border" />
        <span className="text-[9px] font-black uppercase tracking-[0.3em] text-muted-foreground flex items-center gap-2 whitespace-nowrap">
          {isVisible
            ? <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            : <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40" />
          }
          {isVisible ? `Loading in ${countdown}s...` : 'Continue Reading'}
        </span>
        <div className="h-px flex-1 bg-border" />
      </div>

      {/* Article Preview Card */}
      <div className="border border-border hover:border-brand/40 transition-colors bg-background overflow-hidden">

        {/* Progress bar — fills over 4 seconds */}
        <div className="h-[3px] w-full bg-muted relative overflow-hidden">
          <div
            className="absolute left-0 top-0 h-full bg-brand"
            style={{
              width: `${progress}%`,
              transition: isVisible ? 'width 1s linear' : 'none'
            }}
          />
        </div>

        <div className="p-5">
          <div className="flex items-center gap-3 mb-3">
            <p className="text-[9px] font-black uppercase tracking-[0.3em] text-muted-foreground">
              Loading Next Story
            </p>
            {category && (
              <span className="text-[8px] font-black uppercase tracking-widest px-2 py-0.5 bg-brand text-white">
                {category}
              </span>
            )}
          </div>

          <h3 className="font-serif text-lg md:text-2xl font-bold leading-snug mb-3">
            {nextArticle.title}
          </h3>

          {nextArticle.description && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
              {nextArticle.description}
            </p>
          )}

          <div className="flex items-center justify-between">
            {nextArticle.author && (
              <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-mono">
                {nextArticle.author.name}
              </span>
            )}
            {isVisible && (
              <div className="flex items-center gap-1.5">
                {[0, 1, 2].map(i => (
                  <span
                    key={i}
                    className="w-1.5 h-1.5 rounded-full bg-brand animate-bounce"
                    style={{ animationDelay: `${i * 150}ms` }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
